import AuthService from './AuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';

// Helper function to get headers with auth
const getHeaders = (includeContentType = true) => {
  const headers = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const authHeader = AuthService.getAuthHeader();
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }
  return headers;
};

/**
 * DocumentService - Handles document/knowledge base operations
 */
class DocumentService {

  /**
   * Upload a document to the knowledge base
   * @param {File} file - The file to upload
   * @param {string} path - Optional folder path for organizing the document
   * @param {string} connectedSystemId - Optional connected system ID for scoping the document
   * @returns {Promise<Object>} Upload response with documentId, s3Key, chunksCreated, etc.
   */
  static async uploadDocument(file, path = '', connectedSystemId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (path) {
        formData.append('path', path);
      }
      if (connectedSystemId) {
        formData.append('connectedSystemId', connectedSystemId);
      }

      const response = await fetch(`${API_BASE_URL}/v1/documents/upload`, {
        method: 'POST',
        headers: getHeaders(false), // Don't set Content-Type for FormData
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  /**
   * Upload a document with metadata (wizard-based upload)
   * @param {File} file - The file to upload
   * @param {Object} metadata - Document metadata
   * @param {string} metadata.title - Document title (optional, defaults to filename)
   * @param {string} metadata.description - Document description
   * @param {string} metadata.categoryCode - Category: SOP, POLICY, GUIDE, TRAINING, MANUAL, OTHER
   * @param {string} metadata.visibilityCode - Visibility: GLOBAL, TENANT, WAREHOUSE, PRIVATE
   * @param {string} metadata.tags - Comma-separated tags
   * @param {string} connectedSystemId - Connected system ID for scoping
   * @returns {Promise<Object>} Upload response with documentId, title, etc.
   */
  static async uploadDocumentWithMetadata(file, metadata = {}, connectedSystemId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      if (metadata.title) {
        formData.append('title', metadata.title);
      }
      if (metadata.description) {
        formData.append('description', metadata.description);
      }
      if (metadata.categoryCode) {
        formData.append('categoryCode', metadata.categoryCode);
      }
      if (metadata.visibilityCode) {
        formData.append('visibilityCode', metadata.visibilityCode);
      }
      if (metadata.tags) {
        formData.append('tags', metadata.tags);
      }
      if (connectedSystemId) {
        formData.append('connectedSystemId', connectedSystemId);
      }

      const response = await fetch(`${API_BASE_URL}/v1/documents/upload-with-metadata`, {
        method: 'POST',
        headers: getHeaders(false), // Don't set Content-Type for FormData
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading document with metadata:', error);
      throw error;
    }
  }

  /**
   * List accessible documents (with access control)
   * @param {string} connectedSystemId - Optional connected system ID for filtering
   * @returns {Promise<Object>} Object with documents array and total count
   */
  static async listAccessibleDocuments(connectedSystemId = null) {
    try {
      const url = connectedSystemId
        ? `${API_BASE_URL}/v1/documents/accessible?connectedSystemId=${encodeURIComponent(connectedSystemId)}`
        : `${API_BASE_URL}/v1/documents/accessible`;

      const response = await fetch(url, {
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error listing accessible documents:', error);
      throw error;
    }
  }

  /**
   * List documents by category
   * @param {string} categoryCode - Category code: SOP, POLICY, GUIDE, etc.
   * @param {string} connectedSystemId - Optional connected system ID
   * @returns {Promise<Object>} Object with documents array
   */
  static async listDocumentsByCategory(categoryCode, connectedSystemId = null) {
    try {
      const url = connectedSystemId
        ? `${API_BASE_URL}/v1/documents/category/${encodeURIComponent(categoryCode)}?connectedSystemId=${encodeURIComponent(connectedSystemId)}`
        : `${API_BASE_URL}/v1/documents/category/${encodeURIComponent(categoryCode)}`;

      const response = await fetch(url, {
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error listing documents by category:', error);
      throw error;
    }
  }

  /**
   * Search documents by keyword in the database
   * @param {string} query - Search query
   * @param {string} connectedSystemId - Optional connected system ID
   * @returns {Promise<Object>} Object with documents array
   */
  static async searchDocumentsByKeyword(query, connectedSystemId = null) {
    try {
      let url = `${API_BASE_URL}/v1/documents/search-db?query=${encodeURIComponent(query)}`;
      if (connectedSystemId) {
        url += `&connectedSystemId=${encodeURIComponent(connectedSystemId)}`;
      }

      const response = await fetch(url, {
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching documents by keyword:', error);
      throw error;
    }
  }

  /**
   * Get supported file types for document upload
   * @returns {Promise<Object>} Object with extensions array and maxFileSize
   */
  static async getSupportedFileTypes() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/documents/supported-types`, {
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting supported file types:', error);
      throw error;
    }
  }

  /**
   * List user's files from the knowledge base
   * @returns {Promise<Object>} Object with files array, total count, and usage info
   */
  static async listDocuments() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/documents`, {
        headers: getHeaders(false),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error listing documents:', error);
      throw error;
    }
  }

  /**
   * Delete a document from the knowledge base
   * @param {string} docId - The document ID to delete
   * @returns {Promise<Object>} Delete response with success status
   */
  static async deleteDocument(docId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/documents/${encodeURIComponent(docId)}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  /**
   * Get a presigned download URL for a document
   * @param {string} docId - The document ID to download
   * @returns {Promise<string>} The presigned S3 download URL
   */
  static async getDocumentDownloadUrl(docId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/documents/${encodeURIComponent(docId)}/download`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.downloadUrl;
    } catch (error) {
      console.error('Error getting document download URL:', error);
      throw error;
    }
  }

  /**
   * Search documents in the knowledge base
   * @param {string} question - The search query
   * @param {number} topK - Number of results to return (default 5)
   * @returns {Promise<Object>} Search results with text, score, and metadata
   */
  static async searchDocuments(question, topK = 5) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/documents/search`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ question, topK }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching documents:', error);
      throw error;
    }
  }
}

export default DocumentService;
